import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';

import { BrandComponent } from '../-components/brand-component';
import { DeleteBrandDialog } from '../-components/brand-delete-brand-dialog';
import { AddBrandDialog } from '../../../../../../components/add-brand-dialog';
import { Button } from '../../../../../../components/buttons/button';
import { ToastVariant } from '../../../../../../components/toast';
import { type Brand } from '../../../../../../entity/api/brand';
import { OrganizationUserRole } from '../../../../../../entity/api/organization-user';
import { ForbiddenError } from '../../../../../../error';
import { convertError } from '../../../../../../error/convert';
import { addTrackCrumb, trackError } from '../../../../../../error/tracking';
import { captureException } from '../../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../../sdk/request';
import {
  getAuthUserFromCache,
  useArticlesByOrgId,
  useOrganizationBilling,
  useOrganizationUsersList,
  useUpdateUser,
} from '../../../../../../services/storage';
import {
  useBrandsByOrgId,
  useCreateBrand,
  useRemoveBrand,
} from '../../../../../../services/storage/brands-queries';
import { useOrganizationById } from '../../../../../../services/storage/organizations-queries';
import { tNamespace } from '../../../../../../services/translation';
import { createBrandCreateData, setSelectedBrand } from '../../../../../../shared/brand';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import {
  isOrgCancelled,
  isNotSubscribed as orgIsNotSubscribed,
} from '../../../../../../shared/subscription';

const t = tNamespace('app.settings.organization.brands');
const tErrors = tNamespace('errors');

type BrandSearch = {
  addNewBrand: boolean;
};

export const Route = createFileRoute(
  '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/brands'
)({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/brands',
      level: 'info',
    });
  },
  validateSearch: function (search: Record<string, unknown>): BrandSearch {
    return {
      addNewBrand: search.addNewBrand == true,
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: BrandsTab,
});

export function BrandsTab() {
  const { id } = Route.useParams() as unknown as { id: string };
  const { addNewBrand } = Route.useSearch() as unknown as { addNewBrand?: boolean };
  const [isOwner, setIsOwner] = useState(false);
  const addToast = useToast();
  const navigate = useNavigate();
  const [openBrandDeleteDialog, setOpenBrandDeleteDialog] = useState(false);
  const [openAddBrandDialog, setOpenAddBrandDialog] = useState(addNewBrand ?? false);
  const [brandToDelete, setBrandToDelete] = useState<Brand | null>(null);

  const queryClient = getQueryClient();
  // TanStack Query hooks
  const currentUser = getAuthUserFromCache(queryClient);
  const { data: organization } = useOrganizationById(id);
  const { data: organizationUsersData } = useOrganizationUsersList(id);
  const { data: orgBillingStatus } = useOrganizationBilling(id);
  const updateUserMutation = useUpdateUser();
  const { data: brandsData } = useBrandsByOrgId(id);

  // TanStack Query mutations
  const createBrandMutation = useCreateBrand();
  const removeBrandMutation = useRemoveBrand();

  const { data: articlesQuery } = useArticlesByOrgId(id);
  const articleList = articlesQuery?.articles ?? [];

  const brands = brandsData ?? [];
  const isDeletable = brands.length > 1;
  const isNotSubscribed = orgIsNotSubscribed(orgBillingStatus);
  const isCancelled = isOrgCancelled(orgBillingStatus);

  useEffect(
    function () {
      const organizationUsers = organizationUsersData ?? [];
      if (organizationUsers.length === 0 || currentUser == null) {
        return;
      }

      const currentUserOrganization = organizationUsers.find(function (orgUser) {
        return orgUser?.userId === currentUser._id;
      });
      setIsOwner(currentUserOrganization?.role === OrganizationUserRole.Owner);
    },
    [currentUser, organizationUsersData]
  );

  function handleSelectDeleteBrand(brandId: string) {
    const selectedBrand = brands.find(function (brand) {
      return brand?._id === brandId;
    });

    if (selectedBrand == null) {
      return;
    }

    setBrandToDelete(selectedBrand);
    setOpenBrandDeleteDialog(true);
  }

  function handleDeleteBrand() {
    setOpenBrandDeleteDialog(false);
    if (brandToDelete == null) {
      return;
    }

    removeBrandMutation.mutate(brandToDelete._id, {
      onSuccess: function () {
        addToast({
          id: Date.now(),
          title: t('brandDeleted'),
          variant: ToastVariant.Success,
        });
      },
      onError: function (err) {
        addToast({
          id: Date.now(),
          title: t('brandDeletedError'),
          variant: ToastVariant.Error,
        });
        captureException(
          err,
          {
            file: 'app/settings/_sidebar-settings-layout/organization.$id/brands.tsx',
            method: 'handleDeleteBrand',
            infoMessage: 'unable to delete brand',
          },
          trackError
        );
      },
    });
  }

  function handleAddBrand(data: { name: string; websiteUrl: string }) {
    if (organization == null) {
      return;
    }

    const { name, websiteUrl } = data;
    const brandData = createBrandCreateData(organization._id, name, websiteUrl);

    createBrandMutation.mutate(
      { data: brandData, isFromOnboarding: false },
      {
        onSuccess: function (createdBrand) {
          addToast({
            id: Date.now(),
            title: t('brandCreated'),
            variant: ToastVariant.Success,
          });

          const brandID = createdBrand?._id;
          if (brandID == null) {
            return;
          }

          setSelectedBrand(brandID).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/_screen/no-brand.tsx',
                method: 'handleAddBrand',
                infoMessage: 'unable to set selected brand',
              },
              trackError
            );
          });

          updateUserMutation.mutate(
            { selectedBrandId: brandID },
            {
              onSuccess: function () {
                void navigate({ to: '/app' });
              },
              onError: function (err) {
                captureException(
                  err,
                  {
                    file: 'app/settings/_sidebar-settings-layout/organization.$id/brands.tsx',
                    method: 'handleAddBrand',
                    infoMessage: 'unable to set selected brand after creating',
                  },
                  trackError
                );
              },
            }
          );
        },
        onError: function (err) {
          const handledErr = convertError(err);

          // handle forbiddens (which comes from plan features limitations)
          // as such, the original message is a specific enum incoming
          if (handledErr instanceof ForbiddenError) {
            addToast({
              variant: ToastVariant.Error,
              title: tErrors(handledErr.originalMessage),
              id: Date.now(),
            });
            return;
          }

          addToast({
            id: Date.now(),
            title: t('cannotCreateBrand'),
            variant: ToastVariant.Error,
          });
          captureException(
            err,
            {
              file: 'app/settings/_sidebar-settings-layout/organization.$id/brands.tsx',
              method: 'handleAddBrand',
              infoMessage: 'unable to create brand',
            },
            trackError
          );
        },
      }
    );
  }

  function handleEditBrand(brandId: string) {
    updateUserMutation.mutate(
      { selectedBrandId: brandId },
      {
        onSuccess: function () {
          navigate({
            to: '/app/brand-context',
            search: { brandId: brandId },
          }).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/settings/_sidebar-settings_layout/organization.$id/brands.tsx',
                method: 'handleEditBrand',
                infoMessage: 'unable to navigate to /app/brand-context',
              },
              trackError
            );
          });
        },
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/settings/_sidebar-settings_layout/organization.$id/brands.tsx',
              method: 'handleEditBrand',
              infoMessage: 'unable to edit brand',
            },
            trackError
          );
        },
      }
    );
  }

  function handleCreateContent(brandId: string) {
    if (isNotSubscribed || isCancelled) {
      return;
    }
    updateUserMutation.mutate(
      { selectedBrandId: brandId },
      {
        onSuccess: function () {
          navigate({ to: '/app/blogs' }).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/settings/_sidebar-settings_layout/organization.$id/brands.tsx',
                method: 'handleCreateContent',
                infoMessage: 'unable to navigate to /app/blogs',
              },
              trackError
            );
          });
        },
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/settings/_sidebar-settings_layout/organization.$id/brands.tsx',
              method: 'handleCreateContent',
              infoMessage: 'unable to set selected brand',
            },
            trackError
          );
        },
      }
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6 pb-20">
        <div className="flex items-center justify-between">
          <p className="font-display-4">{t('brands')}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenAddBrandDialog(true)}
            leftIcon={<Plus width={16} height={16} />}
          >
            {t('createBrand')}
          </Button>
        </div>
        <div className="flex flex-col gap-4 rounded-md border border-branding-gray p-4">
          {brands.map(function (brand) {
            if (brand == null) {
              return null;
            }

            const numberOfContents = articleList.filter(function (article) {
              return article?.brandId === brand._id;
            }).length;

            return (
              <BrandComponent
                key={brand._id}
                brandInformation={{ _id: brand._id, name: brand.name }}
                isOwner={isOwner}
                onRemove={() => handleSelectDeleteBrand(brand._id)}
                onEdit={function () {
                  handleEditBrand(brand._id);
                }}
                onCreateContent={function () {
                  handleCreateContent(brand._id);
                }}
                isDeletable={isDeletable}
                numberOfContents={numberOfContents}
                canCreateContent={!isNotSubscribed || !isCancelled}
              />
            );
          })}
        </div>
      </div>

      <DeleteBrandDialog
        brandName={brandToDelete?.name ?? ''}
        open={openBrandDeleteDialog}
        onOpenChange={setOpenBrandDeleteDialog}
        onDeleteBrand={handleDeleteBrand}
      />
      <AddBrandDialog
        open={openAddBrandDialog}
        onOpenChange={setOpenAddBrandDialog}
        onAddBrand={handleAddBrand}
      />
    </>
  );
}
